import _ from "lodash";
import api from "CommonCore/api";
import {
  empty,
  emptyOrFalse,
  emptyOrTrue,
  setupLookups,
} from "App/shk-form-tab";
import {
  ASTMODUSKEY,
  getModusFromAntrag,
} from "App/constants";
import {
  schema as studienSchema,
} from "Apis/person-studienbereich-api";

const istKeinTarifvertrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => (modus !== undefined ? modus : antrag && getModusFromAntrag(antrag)) !== ASTMODUSKEY.Tarif,
};

const istTarifvertrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => (modus !== undefined ? modus : antrag && getModusFromAntrag(antrag)) === ASTMODUSKEY.Tarif,
};

// mögliche Werte für lbs_pension_multi
const JaNeinLaufend = [
  { value: 1, label: "Ja" },
  { value: 0, label: "Nein" },
  { value: 2, label: "Antragsverfahren läuft" },
];

const lookupKVArtRelevant = {
  type: "select",
  choices: lookup => lookup && _.map(_.filter(lookup, l => l.kva_relevant === true), l => ({ value: l.kva_id, label: l.kva_kbez })),
  inject: ["k_kvartLookup"],
  selector: lookup => id => lookup && lookup[id] ? lookup[id].kva_kbez : "",
};

const lookupPensionsgrundRelevant = {
  type: "select",
  choices: lookup => lookup && _.map(_.filter(lookup, l => l.kpg_relevant === true), l => ({ value: l.kpg_id, label: l.kpg_kbez })),
  inject: ["k_pensionsgrundLookup"],
  selector: lookup => id => lookup && lookup[id] ? lookup[id].kpg_kbez : "",
};

const studiumFelder = ["psb_student_gueltigab", "psb_student_gueltigbis", "psb_ksbid", "psb_semester", "psb_pfaid"];
function getTarifStudiumfelder() {
  // Bestimmte Felder aus dem Studium-Schema übernehmen
  return studienSchema.table.filter(x => studiumFelder.includes(x.field));
}


export const schema = {
  displayName: "Person/LBV Status",
  table: [
    {
      field: "info_rente",
      display: "Die <b>Rentenversicherungsnummer</b> ist für die Sozialversicherung von großer Bedeutung. Die Rentenversicherungsnummer wird von der Deutschen Rentenversicherung"
        + " (z.B. Bund, Rheinland, Westfalen oder Knappschaft-Bahn-See) durch Übersendung des <b>Versicherungsnummernachweises, ehemals Sozialversicherungsausweises</b> mitgeteilt.",
      type: "plaintext",
      useHeader: false,
      subtag: "rentenr",
    },
    {
      field: "lbs_rvnr_jn",
      display: "Haben Sie bereits eine Rentenversicherungsnummer?",
      required: true,
      lookup: "boolean.radioheader2",
      subtag: "rentenr",
      dependends: [{ fields: ["lbs_rvnr"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "lbs_rvnr",
      display: "Rentenversicherungsnr.",
      maxlength: 12,
      subtag: "rentenr",
    },

    // ------------------------------- Versicherung ------------------

    {
      field: "lbs_kv_jn",
      display: "Ich bin ab Beginn der Beschäftigung in der gesetzlichen Krankenversicherung versichert",
      lookup: "boolean.radioheader2",
      dependends: [
        { fields: ["lbs_kvaid"], invisibleIf: emptyOrFalse },
        { fields: ["lbs_kvprivat_gueltigab"], invisibleIf: emptyOrTrue },
        { fields: ["lbs_kvid"], invisibleIf: empty },
        { fields: ["info_kv_privat"], invisibleIf: emptyOrTrue },
      ],
      subtag: "versicherung",
    },
    {
      field: "lbs_kvaid",
      display: "Art der Versicherung",
      lookup: { settings: lookupKVArtRelevant },
      subtag: "versicherung",
    },
    {
      field: "lbs_kvid",
      display: entity => ` ${ entity?.lbs_kv_jn ? "Krankenkasse" : "Davor war ich zuletzt bei folgender gesetzlicher Krankenkasse versichert" }`,
      lookup: "k_krankenkasse.select.kv_id.kv_bez",
      subtag: "versicherung",
    },
    {
      field: "info_kv_privat",
      display: "Zusatz für freiwilig und privat Versicherte: Der Arbeitgeberzuschuss zur freiwilligen Kranken- und Pflegeversicherung nach § 257 SGB V und § 61 SGB XI kann nur nach Abgabe einer entsprechenden Erklärung (Vordruck ist beim LBV NRW erhältlich) und Vorlage der Beitragsbescheinigungen gewährt werden. Auf einen einmal gewährten Zuschuss kann für die Dauer der Beschäftigung nicht mehr verzichtet werden, es sei denn, es tritt Kranken-/Pflegeversicherungspflicht ein.",
      type: "plaintext",
      useHeader: false,
      subtag: "versicherung",
    },
    {
      field: "lbs_kvprivat_gueltigab",
      display: "privat versichert seit",
      type: "date",
      subtag: "versicherung",
    },

    // ------------------------------- Stichtag ------------------

    {
      field: "lbs_stichtag_an_jn",
      display: "stand ich als Arbeitnehmerin bzw. Arbeitnehmer (nicht als Beamtin bzw. Beamter) in einem Beschäftigungsverhältnis",
      lookup: "boolean.radioheader2",
      subtag: "stichtag",
    },
    {
      field: "lbs_stichtag_entgeltgrenze_jn",
      display: "habe ich ein Einkommen über der Jahresarbeitsentgeltgrenze 2002 (monatlich 3.375 Euro bzw. jährlich 40.500 Euro) bezogen und war deshalb nicht versicherungspflichtig in der gesetzlichen Krankenversicherung (falls ja, bitte Nachweise beifügen)",
      lookup: "boolean.radioheader2",
      subtag: "stichtag",
    },
    {
      field: "lbs_stichtag_kv_jn",
      display: "war ich bei einem privaten Krankenversicherungsunternehmen in einer Krankheitsvollkostenversicherung versichert (falls ja, bitte Nachweise beifügen)",
      lookup: "boolean.radioheader2",
      subtag: "stichtag",
    },

    // ----------------------------------- C. Pflegeversicherung --------------------------------------

    {
      field: "lbs_kind_jn",
      display: "3.1 Ich habe ein leibliches Kind bzw. leibliche Kinder (darunter fallen auch Adoptivkinder)",
      lookup: "boolean.radioheader2",
      subtag: "pflege",
      dependends: [
        { fields: ["lbs_kind_anzahl"], invisibleIf: emptyOrFalse },
        { fields: ["info_kind_leiblich"], invisibleIf: emptyOrFalse },
      ],
    },
    {
      field: "lbs_kind_anzahl",
      display: "Anzahl leiblicher Kinder unter 25 Jahren",
      type: "number",
      required: entity => entity.lbs_kind_jn === true,
      subtag: "pflege",
      validate: (value, entity) => {
        if ((!value || isNaN(value)) && entity.lbs_kind_jn === true) {
          return "Die Anzahl der Kinder muss angegeben werden.";
        }
      },
    },
    {
      field: "info_kind_leiblich",
      display: "Bitte Nachweise beifügen, z.B. Geburtsurkunde, Urkunde über der Anerkennung oder Feststellung der Vaterschaft etc.",
      type: "plaintext",
      useHeader: false,
      subtag: "pflege",
    },
    // --
    {
      field: "lbs_kind_aufgenommen_jn",
      display: "3.2 Ich habe (oder habe früher) ein Kind bzw. mehrere Kinder in meinem Haushalt aufgenommen",
      lookup: "boolean.radioheader2",
      subtag: "pflege",
      dependends: [{ fields: ["lbs_stiefkind_jn", "lbs_pflegekind_jn"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "lbs_stiefkind_jn",
      display: "Stiefkind/er",
      lookup: "boolean.radioheader2",
      subtag: "pflege",
      dependends: [
        { fields: ["lbs_stiefkind_anzahl", "info_kind_stief"], invisibleIf: emptyOrFalse },
      ],
    },
    {
      field: "lbs_stiefkind_anzahl",
      display: "Anzahl Stiefkinder unter 25 Jahren",
      required: entity => entity.lbs_stiefkind_jn === true,
      type: "number",
      subtag: "pflege",
      noContainer: true,
      validate: (value, entity) => {
        if ((!value || isNaN(value)) && entity.lbs_stiefkind_jn === true) {
          return "Die Anzahl der Kinder muss angegeben werden.";
        }
      },
    },
    {
      field: "info_kind_stief",
      display: "Als Nachweise bitte Ihre Heiratsurkunde und die Geburtsurkunde des Kindes sowie eine Melde- bzw. Haushaltsbescheinigung beifügen",
      type: "plaintext",
      useHeader: false,
      subtag: "pflege",
      noContainer: true,
    },
    // --
    {
      field: "lbs_pflegekind_jn",
      display: "Pflegekind/er",
      lookup: "boolean.radioheader2",
      subtag: "pflege",
      dependends: [
        { fields: ["lbs_pflegekind_anzahl"], invisibleIf: emptyOrFalse },
        { fields: ["info_kind_pflege"], invisibleIf: emptyOrFalse },
      ],
    },
    {
      field: "lbs_pflegekind_anzahl",
      display: "Anzahl Pflegekinder unter 25 Jahren",
      required: entity => entity.lbs_pflegekind_jn === true,
      type: "number",
      subtag: "pflege",
      validate: (value, entity) => {
        if ((!value || isNaN(value)) && entity.lbs_pflegekind_jn === true) {
          return "Die Anzahl der Kinder muss angegeben werden.";
        }
      },
    },
    {
      field: "info_kind_pflege",
      display: "Als Nachweise bitte Geburtsurkunde des Kindes und eine Melde- bzw. Haushaltsbescheinigung sowie eine Bescheinigung des Jugendamtes über das Pflegeverhältnis beifügen.",
      type: "plaintext",
      useHeader: false,
      subtag: "pflege",
    },
    // ----------------------------------- D. Rentenversicherung --------------------------------------

    {
      field: "lbs_rv_jn",
      display: "Ich bin für diese Beschäftigung von der gesetzlichen Rentenversicherungspflicht befreit",
      lookup: "boolean.radioheader2",
      dependends: [{ fields: ["info_rv", "lbs_mitglied_ve_jn"], invisibleIf: emptyOrFalse }],
      subtag: "rente",
    },
    {
      field: "info_rv",
      display: "Bitte Befreiungsbescheid der Deutschen Rentenversicherung für dieses Beschäftigungsverhältnis vorlegen.",
      type: "plaintext",
      useHeader: false,
      subtag: "rente",
    },
    {
      field: "lbs_mitglied_ve_jn",
      lookup: "boolean.radioheader2",
      display: "Ich bin Mitglied in einer berufsständischen Versorgungs- oder Versicherungseinrichtung",
      dependends: [{ fields: ["info_versorgung", "lbs_versorgungseinrichtung", "lbs_vemitgliedsnr"], invisibleIf: emptyOrFalse }],
      subtag: "rente",
    },
    {
      field: "info_versorgung",
      display: "Bitte Mitgliedsurkunde beifügen.",
      type: "plaintext",
      useHeader: false,
      subtag: "rente",
    },

    {
      field: "lbs_versorgungseinrichtung",
      display: "Mitglied bei",
      subtag: "rente",
    },
    {
      field: "lbs_vemitgliedsnr",
      display: "Mitglieds-/Vers.Nr.",
      subtag: "rente",
    },


    // ------------------------------- Weitere Einkommen ---------------------------------------------

    {
      field: "lbs_rente_jn",
      display: "Ich beziehe Rente oder habe eine Rente beantragt",
      lookup: "boolean.radioheader2",
      dependends: [{ fields: ["lbs_renteseit", "lbs_rentenart", "info_rentenart", "lbs_rententraeger", "lbs_versorgungsnummer", "lbs_rentekv"], invisibleIf: emptyOrFalse }],
      subtag: "weiteree",
    },
    {
      field: "lbs_renteseit",
      display: "Seit",
      subtag: "weiteree",
    },
    {
      field: "lbs_rentenart",
      display: "Rentenart",
      subtag: "weiteree",
    },

    {
      field: "info_rentenart",
      display: "<b>Hinweis:</b>: Bitte Kopie der 1. und 2. Seite des Rentenbescheides (Beginn und Art der Rente) und ggf. der letzten Rentenanpassungsmitteilung beifügen.",
      type: "plaintext",
      useHeader: false,
    },


    {
      field: "lbs_rententraeger",
      display: "Versicherungsträger mit Anschrift",
      subtag: "weiteree",
    },
    {
      field: "lbs_versorgungsnummer",
      display: "Versicherungs- oder Versorgungsnummer",
      subtag: "weiteree",
    },
    {
      field: "lbs_rentekv",
      display: "Krankenversicherung der Rentner bei",
      subtag: "weiteree",
    },
    {
      field: "lbs_pension_jn",
      display: "Ich erhalte Versorgungsbezüge nach beamtenrechtlichen Grundsätzen",
      lookup: "boolean.radioheader2",
      dependends: [{
        fields: ["lbs_pension_gueltigab", "lbs_pension_dienststelle", "lbs_pension_az", "lbs_pension_azv", "lbs_kpgid"],
        invisibleIf: emptyOrFalse,
      }],
      hideFromDetail: istTarifvertrag,
      hideFromEditor: istTarifvertrag,
      subtag: "weiteree",
    },
    {
      field: "lbs_pension_multi",
      display: "Ich erhalte Versorgungsbezüge nach beamtenrechtlichen Grundsätzen",
      type: "radioheader2",
      noSort: true,
      selector: value => _.find(JaNeinLaufend, opt => opt.value === value),
      dependends: [
        {
          fields: ["lbs_pension_gueltigab"],
          invisibleIf: value => value !== 1,
        }, // nicht "Ja..."
        {
          fields: ["lbs_pension_antrag_ab"],
          invisibleIf: value => value !== 2,
        }, // nicht "laufend..."
        {
          fields: ["lbs_pension_dienststelle", "lbs_pension_dienststelle_anschrift", "lbs_pension_az", "lbs_pension_azv"],
          invisibleIf: value => value === 0,
        }, // unsichtbar wenn "Nein"
      ],
      inject: ["flag_antragstellermodus", "currentAntrag"],
      choices: JaNeinLaufend,
      hideFromDetail: istKeinTarifvertrag,
      hideFromEditor: istKeinTarifvertrag,
      subtag: "weiteree",
    },
    {
      field: "lbs_pension_gueltigab",
      display: "Seit",
      type: "date",
      subtag: "weiteree",
    },
    {
      field: "lbs_pension_antrag_ab",
      display: "Antragsdatum",
      type: "date",
      syncValue: "lbs_pension_gueltigab",
      subtag: "weiteree",
    },
    {
      field: "lbs_pension_dienststelle",
      display: "Die Bezüge zahlt (Name der Dienststelle)",
      subtag: "weiteree",
    },
    {
      field: "lbs_pension_dienststelle_anschrift",
      display: "Anschrift der Dienststelle",
      inject: ["flag_antragstellermodus", "currentAntrag"],
      hideFromDetail: istKeinTarifvertrag,
      hideFromEditor: istKeinTarifvertrag,
      subtag: "weiteree",
    },
    {
      field: "lbs_pension_az",
      display: "Aktenzeichen / Personalnummer",
      subtag: "weiteree",
      inject: ["flag_antragstellermodus", "currentAntrag"],
      hideFromDetail: istTarifvertrag,
      hideFromEditor: istTarifvertrag,
    },
    {
      field: "lbs_pension_azv",
      display: "Aktenzeichen / Personalnummer / Versorgungsnummer",
      subtag: "weiteree",
      syncValue: "lbs_pension_az",
      inject: ["flag_antragstellermodus", "currentAntrag"],
      hideFromDetail: istKeinTarifvertrag,
      hideFromEditor: istKeinTarifvertrag,
    },
    {
      field: "lbs_kpgid",
      display: "Grund",
      lookup: { settings: lookupPensionsgrundRelevant },
      subtag: "weiteree",
      hideFromDetail: istTarifvertrag,
      hideFromEditor: istTarifvertrag,
    },
    {
      field: "lbs_verzicht_rvfreiheit",
      display: "Ich verzichte gem. § 5 Abs. 4 SGB VI für diese Beschäftigung auf die Rentenversicherungsfreiheit.",
      lookup: "boolean.radioheader2",
      dependends: [{ fields: ["info_verzicht_rvfreiheit"], invisibleIf: emptyOrFalse }],
      subtag: "weiteree",
    },
    {
      field: "info_verzicht_rvfreiheit",
      display: "Hinweis zur Erklärung zum Verzicht auf die Rentenversicherungsfreiheit:<br />Mir ist bekannt, dass der Verzicht nur mit Wirkung für die Zukunft erklärt werden kann, für die Dauer der Beschäftigung bindend und eine Rücknahme nicht möglich ist.",
      type: "plaintext",
      useHeader: false,
      subtag: "weiteree",
    },

    /*
     * ------------------- Praktikum ---------------------
     * --> Student
     */
    {
      field: "lbs_student_jn",
      display: "Ich bin Studentin bzw. Student",
      lookup: "boolean.radioheader2",
      subtag: "studium",
      hideFromDetail: istKeinTarifvertrag,
      hideFromEditor: istKeinTarifvertrag,
      dependends: [{ fields: studiumFelder, invisibleIf: emptyOrFalse }],
    },
    ...getTarifStudiumfelder(),
    {
      field: "lbs_praktikum_jn",
      display: "Ich bin Praktikantin bzw. Praktikant",
      lookup: "boolean.radioheader2",
      subtag: "studium",
      dependends: [{ fields: ["info_praktikum", "lbs_praktikum_gueltigab", "lbs_praktikum_gueltigbis", "lbs_pflicht_jn", "lbs_praktikum_verguetung_jn"], invisibleIf: emptyOrFalse }],
    },

    {
      field: "lbs_praktikum_gueltigab",
      display: "seit",
      type: "date",
      subtag: "studium",
    },
    {
      field: "lbs_praktikum_gueltigbis",
      display: "bis voraussichtlich",
      type: "date",
      subtag: "studium",
    },
    {
      field: "lbs_pflicht_jn",
      display: "Handelt es sich um eine in Studien- oder Prüfungsordungen vorgeschriebene berufspraktische Tätigkeit?",
      lookup: "boolean.radioheader2",
      subtag: "studium",
    },
    {
      field: "info_praktikum",
      display: "Bitte Praktikantenvertrag, Studienbescheinigung und Auszug aus der Studien-/Prüfungsordnung beifügen",
      type: "plaintext",
      subtag: "studium",
    },

    {
      field: "lbs_praktikum_verguetung_jn",
      display: "Ich erhalte Praktikantenvergütung",
      lookup: "boolean.radioheader2",
      subtag: "studium",
      dependends: [{ fields: ["lbs_praktikum_verguetung_gueltigab", "lbs_praktikum_verguetung_gueltigbis", "lbs_praktikum_gehalt", "lbs_praktikum_std", "lbs_praktikum_tage"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "lbs_praktikum_verguetung_gueltigab",
      display: "seit",
      type: "date",
      subtag: "studium",
    },
    {
      field: "lbs_praktikum_verguetung_gueltigbis",
      display: "voraussichtlich bis",
      type: "date",
      subtag: "studium",
    },
    {
      field: "lbs_praktikum_gehalt",
      display: "Monatliche Vergütung (in EUR)",
      type: "money",
      subtag: "studium",
    },
    {
      field: "lbs_praktikum_std",
      display: "Wöchentliche Arbeitszeit (in Std)",
      type: "number",
      subtag: "studium",
    },
    {
      field: "lbs_praktikum_tage",
      display: "Arbeitstage pro Woche",
      type: "number",
      subtag: "studium",
    },

    // ------------------- Schüler / Studium
    {
      field: "lbs_schueler_jn",
      display: "10.1 Ich bin / war vor Aufnahme der Beschäftigung Schüler*in / Studierende*r",
      lookup: "boolean.radioheader2",
      subtag: "schueler",
      dependends: [{ fields: ["info_schueler"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "info_schueler",
      display: "Bitte Schul-/Studienbescheinigung beifügen",
      type: "plaintext",
      useHeader: false,
      subtag: "schueler",
    },
    // --
    {
      field: "nach_ende",
      type: "plaintext",
      usHeader: false,
      display: "10.2 Nach Ende der Beschäftigung wird",
      subtag: "schueler",
      dependends: [{ fields: ["lbs_ende_beruf_jn", "lbs_ende_fsj_jn", "lbs_ende_ausland_jn", "lbs_ende_sonstiges_jn"], invisibleIf: () => false }],
    },
    {
      field: "lbs_ende_beruf_jn",
      display: "... eine Berufsausbildung aufgenommen",
      lookup: "boolean.radioheader2",
      subtag: "schueler",
      dependends: [{ fields: ["lbs_ende_beruf_ausbildung"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "lbs_ende_beruf_ausbildung",
      display: "Ausbildung zur/zum",
      subtag: "schueler",
    },
    // --
    {
      field: "lbs_ende_fsj_jn",
      display: "... ein freiwilliges soziales, ökologisches Jahr ö.ä. Freiwilligendienst aufgenommen",
      lookup: "boolean.radioheader2",
      dependends: [{ fields: ["lbs_ende_fsj_gueltigab"], invisibleIf: emptyOrFalse }],
      subtag: "schueler",
    },
    {
      field: "lbs_ende_fsj_gueltigab",
      type: "date",
      display: "Freiwilligendienst ab",
      subtag: "schueler",
    },
    // --
    {
      field: "lbs_ende_ausland_jn",
      display: "... ein Auslandsaufenthalt durchgeführt",
      lookup: "boolean.radioheader2",
      subtag: "schueler",
      dependends: [{ fields: ["lbs_ende_ausland_gueltigab"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "lbs_ende_ausland_gueltigab",
      type: "date",
      display: "Auslandsaufenthalt ab",
      subtag: "schueler",
    },
    // --
    {
      field: "lbs_ende_sonstiges_jn",
      display: "sonstiges",
      lookup: "boolean.radioheader2",
      subtag: "schueler",
      dependends: [{ fields: ["lbs_ende_sonstiges_beschreibung", "lbs_ende_sonstiges_gueltigab"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "lbs_ende_sonstiges_beschreibung",
      display: "Beschreibung",
      subtag: "schueler",
    },
    {
      field: "lbs_ende_sonstiges_gueltigab",
      type: "date",
      display: "ab",
      subtag: "schueler",
    },

    // ---------------------------- Arbeitslosigkeit ----------------------------
    {
      field: "lbs_alg_jn",
      display: "Ich erhalte / erhielt unmittelbar vor Beschäftigungsbeginn Leistungen von der Agentur für Arbeit bzw. dem Jobcenter oder habe mich / war als Arbeitssuchender gemeldet",
      lookup: "boolean.radioheader2",
      subtag: "arbeitslos",
      dependends: [{ fields: ["lbs_alg_gueltigab", "lbs_alg_gueltigbis"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "lbs_alg_gueltigab",
      display: "seit",
      type: "date",
      subtag: "arbeitslos",
    },
    {
      field: "lbs_alg_gueltigbis",
      display: "bis",
      type: "date",
      subtag: "arbeitslos",
    },
    // ---------------------------- Zusätzliche Versorgung ----------------------------

    {
      field: "lbs_vbl_jn",
      display: "Ich war bereits bei der Versorgungsanstalt des Bundes und der Länder (VBL) oder einer anderen Zusatzversorgungseinrichtung versichert",
      lookup: "boolean.radioheader2",
      subtag: "versorgung",
      dependends: [{ fields: ["lbs_vbl_name", "lbs_vbl_nummer", "lbs_erstattung_jn", "info_zusatzvs"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "lbs_vbl_name",
      display: "versichert bei",
      subtag: "versorgung",
    },
    {
      field: "lbs_vbl_nummer",
      display: "Versicherungsnummer",
      subtag: "versorgung",
    },
    {
      field: "lbs_erstattung_jn",
      display: "Wurden Beiträge aus einer früheren Versicherung erstattet?",
      lookup: "boolean.radio",
      subtag: "versorgung",
    },
    {
      field: "info_zusatzvs",
      display: `<b>Hinweis:</b><br /> Wenn Sie vorher bei einer anderen Zusatzversorgungseinrichtung (nicht VBL) versichert waren, gibt es die Möglichkeit einen Überleitungsantrag zu stellen. Für weitere Informationen und einen Antragsvordruck wenden Sie sich bitte an Ihre*n zuständige*n Sachbearbeiter*in aus der Personalabteilung.`,
      type: "plaintext",
      useHeader: false,
      inject: ["flag_antragstellermodus", "currentAntrag"],
      hideFromDetail: istKeinTarifvertrag,
      hideFromEditor: istKeinTarifvertrag,
      subtag: "versicherung",
    },
    {
      field: "lbs_vbl_befreit_jn",
      display: "Ich bin von der Versicherungspflicht in der Zusatzversorgung befreit",
      lookup: "boolean.radioheader2",
      subtag: "versorgung",
      dependends: [{ fields: ["info_vbl_befreit"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "info_vbl_befreit",
      display: "Bitte Befreiungsbescheid beifügen.",
      type: "plaintext",
      // useHeader: false,
      subtag: "versorgung",
    },


    // -- Niedriglohn --
    {
      field: "lbs_rvfreiheit_jn",
      display: "Für den Fall, dass meine Beschäftigung als geringfügig entlohnte Beschäftigung zu werten ist, beantrage ich Rentenversicherungsfreiheit",
      lookup: "boolean.radioheader2",
      subtag: "niedriglohn",
      dependends: [{ fields: ["info_rvbefreiung"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "info_rvbefreiung",
      display: `<b>Hinweis:</b> Die Befreiung wirkt grundsätzlich ab Beginn des Kalendermonats des Eingangs beim Arbeitgeber, frühestens ab Beschäftigungsbeginn.
Voraussetzung ist, dass der Arbeitgeber der Minijob-Zentrale die Befreiung bis zur nächsten Entgeltabrechnung, spätestens
innerhalb von 6 Wochen nach Eingang des Befreiungsantrages bei ihm meldet. Anderenfalls beginnt die Befreiung erst nach Ablauf
des Kalendermonats, der dem Kalendermonat des Eingangs der Meldung bei der Minijob-Zentrale folgt.
Die beantragte Befreiung von der Rentenversicherungspflicht kann einmalig auf Antrag des Beschäftigten aufgehoben werden (§ 6 Abs. 6 SGB VI). 
Die Aufhebung der Befreiung von der Versicherungspflicht gilt ab dem Folgemonat der Antragsstellung und ist für die Dauer der Beschäftigung bindend.
Mir ist bekannt, dass der Befreiungsantrag für alle von mir zeitgleich ausgeübten geringfügig entlohnten Beschäftigungen gilt. Ich verpflichte
mich, alle weiteren Arbeitgeber, bei denen ich eine geringfügig entlohnte Beschäftigung ausübe, über diesen Befreiungsantrag
zu informieren. Der Arbeitgeber zahlt Pauschbeträge.`,
      subtag: "niedriglohn",
      type: "plaintext",
      useHeader: false,
    },

    /*
     *{ field: 'lbs_rvgleitzone_jn',
     *  display: 'Für den Fall, dass das LBV feststellt, dass mein Einkommen tatsächlich innerhalb der Gleitzone liegt, erkläre ich, dass ich auf die Reduzierung der Rentenversicherung verzichte und den vollen Beitrag zur Rentenversicherung zahlen möchte',
     *  lookup: 'boolean.radioheader2',
     *  subtag: 'niedriglohn',
     *  dependends: [ { fields: ['lbs_rvgleitzone_beginn_jn'], invisibleIf: emptyOrFalse }, ],
     *},
     *    { field: 'lbs_rvgleitzone_beginn_jn',
     *      display: 'Mein Verzicht auf die Reduzierung des beitragspflichtigen Arbeitsentgelts für den Bereich der Rentenversicherung soll mit Beginn der Beschäftigung wirksam werden',
     *      lookup: 'boolean.radioheader2',
     *      subtag: 'niedriglohn',
     *      dependends: [ { fields: ['lbs_rvgleitzone_gueltigab'], invisibleIf: emptyOrFalse }, ],
     *    },
     *    { field: 'lbs_rvgleitzone_gueltigab',
     *      display: 'Beginn der Wirksamkeit',
     *      type: 'date',
     *      subtag: 'niedriglohn',
     *    },
     */
  ],
};

export function beforeSaveWeiteree(entity, edits) {
  // verhindern, dass lbs_pension_gueltigab in den edits ungewollt auf null gesetzt wird
  if (entity.lbs_pension_antrag_ab
    && edits.lbs_pension_antrag_ab === undefined
    && edits.lbs_pension_gueltigab === undefined) {
    edits.lbs_pension_gueltigab = entity.lbs_pension_antrag_ab;
  }
}

export function beforeSaveVersicherung(entity, edits) {
  const kvJn = edits.lbs_kv_jn !== undefined ? edits.lbs_kv_jn : entity.lbs_kv_jn;

  if (kvJn === true) {
    // Bei gesetzlicher KV sind Privatfelder nicht relevant.
    edits.lbs_kvprivat_gueltigab = null;
  }

  if (kvJn === false) {
    // Bei privater KV ist die Versicherungsart nicht relevant.
    edits.lbs_kvaid = null;
  }

  // Bei Umschalten ohne neue Kassen-Auswahl alte Kasse leeren (Safety-Net, validateEditsVersicherung macht das Gleiche beim Tippen).
  if (edits.lbs_kv_jn !== undefined && edits.lbs_kvid === undefined) {
    edits.lbs_kvid = null;
  }
}

export function validateEditsVersicherung(entity, edits, prevEdits) {
  const previousValue = prevEdits && prevEdits.lbs_kv_jn !== undefined
    ? prevEdits.lbs_kv_jn
    : entity && entity.lbs_kv_jn;
  const newValue = edits.lbs_kv_jn !== undefined ? edits.lbs_kv_jn : previousValue;

  if (edits.lbs_kv_jn !== undefined && newValue !== previousValue) {
    edits.lbs_kvid = null;
  }
}

export function normalizeEditsPflege(entity, edits) {
  const kindAufgenommen = edits.lbs_kind_aufgenommen_jn !== undefined
    ? edits.lbs_kind_aufgenommen_jn
    : entity && entity.lbs_kind_aufgenommen_jn;

  if (kindAufgenommen === false) {
    edits.lbs_stiefkind_jn = false;
    edits.lbs_adoptivkind_jn = false;
    edits.lbs_geschwisterkind_jn = false;
    edits.lbs_pflegekind_jn = false;
  }
}

export function validateEditsPflege(entity, edits, prevEdits) {
  const previousValue = prevEdits && prevEdits.lbs_kind_aufgenommen_jn !== undefined
    ? prevEdits.lbs_kind_aufgenommen_jn
    : entity && entity.lbs_kind_aufgenommen_jn;
  const newValue = edits.lbs_kind_aufgenommen_jn !== undefined ? edits.lbs_kind_aufgenommen_jn : previousValue;

  if (edits.lbs_kind_aufgenommen_jn !== undefined && newValue !== previousValue && newValue === false) {
    normalizeEditsPflege(entity, edits);
  }
}

// Fields anreichern
schema.table = setupLookups(schema);

const desc = {
  id: "p_id",
  name: "lbvperson",
  endpoint: "/api/shkperson/lbvperson",
  schema,
  breadcrumbs: {
    list: "LBV Status",
    detail: ba => ba.ba_kbez,
  },
};

const personlbvstatusApi = api(desc);
export default personlbvstatusApi;
